import { useEffect, useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PAYMENT_METHODS,
  paiseToRupees,
  parseRupees,
  type Alteration,
  type PaymentMethod,
} from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import { useAuth } from '../../lib/auth/auth-context.js';
import { useAlterationPayments, useMoveAlteration } from '../../lib/alterations/use-alterations.js';
import { formatCurrency } from '../../lib/format.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * Handing an alteration back while money is still owed on it.
 *
 * The hand-over is when the cash changes hands, so that is where the balance is asked for: the
 * amount is already filled in, the method is the one this shop uses most, and one button both
 * takes it and hands the garment over — one commit on the server, so a refused payment leaves the
 * garment on the counter rather than half-done.
 *
 * Credit is still allowed. A regular who says "next time" is handed their garment with the
 * second button, and the balance stays on the alteration for the counter to collect later.
 *
 * A tailor can hand one over but cannot take money (`payments.record`), so they are shown only
 * that second button and told what is still due.
 */

function rupeeInputValue(paise: number): string {
  if (paise <= 0) return '';
  const rupees = paiseToRupees(paise);
  return Number.isInteger(rupees) ? String(rupees) : rupees.toFixed(2);
}

export interface HandOverAlterationDialogProps {
  alteration: Alteration | null;
  onOpenChange: (open: boolean) => void;
}

export function HandOverAlterationDialog({
  alteration,
  onOpenChange,
}: HandOverAlterationDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { role } = useAuth();
  const fieldId = useId();
  const move = useMoveAlteration();

  const mayCollect = role !== 'tailor';
  const ledger = useAlterationPayments(mayCollect && alteration ? alteration.id : null);
  const outstandingPaise = ledger.data?.outstandingPaise ?? alteration?.outstandingPaise ?? 0;

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [error, setError] = useState<string | null>(null);

  // Filled in afresh for each garment, with the shop's usual method once the ledger says it.
  useEffect(() => {
    if (!alteration) return;
    setAmount(rupeeInputValue(alteration.outstandingPaise));
    setError(null);
  }, [alteration]);
  useEffect(() => {
    if (ledger.data) setMethod(ledger.data.suggestedMethod);
  }, [ledger.data]);

  const amountPaise = ((): number => {
    if (amount.trim() === '') return 0;
    try {
      return parseRupees(amount);
    } catch {
      return 0;
    }
  })();
  const tooMuch = amountPaise > outstandingPaise;

  const handOver = async (collect: boolean): Promise<void> => {
    if (!alteration) return;
    setError(null);
    try {
      await move.mutateAsync({
        alterationId: alteration.id,
        toStatus: 'delivered',
        ...(collect ? { collect: { amountPaise, method } } : {}),
      });
      toast({
        title: collect
          ? t('alterations.handOver.collected', { amount: formatCurrency(amountPaise) })
          : t('alterations.movedTo.delivered'),
        variant: 'success',
      });
      onOpenChange(false);
    } catch (caught) {
      setError(isApiRequestError(caught) ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog open={alteration !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('alterations.handOver.title', { number: alteration?.number })}
          </DialogTitle>
          <DialogDescription>
            {t('alterations.handOver.due', { amount: formatCurrency(outstandingPaise) })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {error ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
            >
              {error}
            </p>
          ) : null}

          {mayCollect ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${fieldId}-amount`}>{t('payments.amount')}</Label>
                <Input
                  id={`${fieldId}-amount`}
                  inputMode="decimal"
                  autoFocus
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  aria-invalid={tooMuch ? true : undefined}
                />
                {tooMuch ? (
                  <p className="text-sm text-destructive">
                    {t('alterations.handOver.tooMuch', {
                      amount: formatCurrency(outstandingPaise),
                    })}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${fieldId}-method`}>{t('payments.method')}</Label>
                <select
                  id={`${fieldId}-method`}
                  value={method}
                  onChange={(event) => setMethod(event.target.value as PaymentMethod)}
                  className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                >
                  {PAYMENT_METHODS.map((one) => (
                    <option key={one} value={one}>
                      {t(`payments.methods.${one}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('alterations.handOver.counterCollects')}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={move.isPending} onClick={() => void handOver(false)}>
            {t('alterations.handOver.withoutPayment')}
          </Button>
          {mayCollect ? (
            <Button
              disabled={move.isPending || amountPaise <= 0 || tooMuch}
              onClick={() => void handOver(true)}
            >
              {t('alterations.handOver.collectAndDeliver')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default HandOverAlterationDialog;
